import { useAuthStore } from '../store/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { token } = useAuthStore.getState();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    useAuthStore.getState().logout();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'API Error');
  }

  return response.json();
}

export function getLoginOptions() {
  return fetchApi<any>('/auth/login-passkey-options');
}

// Password login - main login method
export function login(username: string, password: string) {
  return fetchApi<{ token: string; user: any }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

// Passkey login - alternative method
export function loginWithPasskey(challengeId: string, response: any) {
  return fetchApi<{ token: string; user: any }>('/auth/login-passkey', {
    method: 'POST',
    body: JSON.stringify({ challengeId, response }),
  });
}

export function logout() {
  return fetchApi('/auth/logout', { method: 'POST' });
}

export function getMe() {
  return fetchApi<any>('/auth/me');
}

export function getTasks(filters?: {
  status?: string;
  priority?: string;
  dueDate?: string;
  dueDateEnd?: string;
  assigneeId?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.priority) params.append('priority', filters.priority);
  if (filters?.dueDate) params.append('dueDate', filters.dueDate);
  if (filters?.dueDateEnd) params.append('dueDateEnd', filters.dueDateEnd);
  if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId.toString());
  const queryString = params.toString();
  const url = queryString ? `/tasks?${queryString}` : '/tasks';
  return fetchApi<any[]>(url);
}

export function getTask(id: number) {
  return fetchApi<any>(`/tasks/${id}`);
}

export function createTask(data: any) {
  return fetchApi<any>('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getAssignableUsers() {
  return fetchApi<any[]>('/tasks/assignable-users');
}

export function getAssignableGroups() {
  return fetchApi<any[]>('/tasks/assignable-groups');
}

export function updateTask(id: number, data: any) {
  return fetchApi<any>(`/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function updateMyTaskStatus(id: number, status: string) {
  return fetchApi<any>(`/tasks/${id}/my-status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function updateAssigneeStatus(id: number, userId: number, status: string) {
  return fetchApi<any>(`/tasks/${id}/assignee/${userId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function getReports(filters?: {
  weekStart?: string;
  status?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.weekStart) params.append('weekStart', filters.weekStart);
  if (filters?.status) params.append('status', filters.status);
  const queryString = params.toString();
  const url = queryString ? `/reports?${queryString}` : '/reports';
  return fetchApi<any[]>(url);
}

export function getTeamReports(filters?: {
  weekStart?: string;
  status?: string;
  userId?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.weekStart) params.append('weekStart', filters.weekStart);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.userId) params.append('userId', filters.userId.toString());
  const queryString = params.toString();
  const url = queryString ? `/reports/team?${queryString}` : '/reports/team';
  return fetchApi<any[]>(url);
}

export function getReport(id: number) {
  return fetchApi<any>(`/reports/${id}`);
}

export function createReport(data: any) {
  return fetchApi<any>('/reports', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateReport(id: number, data: any) {
  return fetchApi<any>(`/reports/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function reviewReport(id: number) {
  return fetchApi<any>(`/reports/${id}/review`, {
    method: 'PATCH',
  });
}

export function getDashboardStats() {
  return fetchApi<any>('/reports/dashboard/stats');
}

export function getCurrentWeekStatus() {
  return fetchApi<any>('/reports/current/status');
}

// Groups API
export function getGroups() {
  return fetchApi<any[]>('/groups');
}

export function getGroup(id: number) {
  return fetchApi<any>(`/groups/${id}`);
}

export function createGroup(data: any) {
  return fetchApi<any>('/groups', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateGroup(id: number, data: any) {
  return fetchApi<any>(`/groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function deleteGroup(id: number) {
  return fetchApi(`/groups/${id}`, {
    method: 'DELETE',
  });
}

// Comments API
export function getTaskComments(taskId: number) {
  return fetchApi<any[]>(`/comments?taskId=${taskId}`);
}

export function getReportComments(reportId: number) {
  return fetchApi<any[]>(`/comments?reportId=${reportId}`);
}

export function createComment(data: { taskId?: number; reportId?: number; content: string }) {
  return fetchApi<any>('/comments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function deleteComment(commentId: number) {
  return fetchApi(`/comments/${commentId}`, {
    method: 'DELETE',
  });
}

// Report revert API
export function revertReportToDraft(reportId: number) {
  return fetchApi<any>(`/reports/${reportId}/revert-to-draft`, {
    method: 'PATCH',
  });
}

// Users management API (Boss only)
export function getUsers() {
  return fetchApi<any[]>('/auth/users');
}

export function getUser(id: number) {
  return fetchApi<any>(`/auth/users/${id}`);
}

export function updateUser(id: number, data: {
  name?: string;
  email?: string;
  username?: string;
  role?: string;
  managerId?: number | null;
  isActive?: boolean;
}) {
  return fetchApi<any>(`/auth/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// Profile API
export function updateMyProfile(data: {
  name?: string;
  email?: string;
  username?: string;
}) {
  return fetchApi('/auth/users/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ========== Password Management ==========

export function changePassword(data: {
  currentPassword: string;
  newPassword: string;
}) {
  return fetchApi('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function resetUserPassword(userId: number, password: string) {
  return fetchApi(`/auth/users/${userId}/password`, {
    method: 'PATCH',
    body: JSON.stringify({ password }),
  });
}

// ========== Passkey Management ==========

export interface PasskeyInfo {
  id: number;
  credentialId: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export function getMyPasskeys() {
  return fetchApi<PasskeyInfo[]>('/auth/my-passkeys');
}

export function getBindPasskeyOptions() {
  return fetchApi<RegisterOptions & { challengeId: string }>('/auth/bind-passkey-options');
}

export function bindPasskey(data: {
  challengeId: string;
  response: any;
  deviceName?: string;
}) {
  return fetchApi('/auth/bind-passkey', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function unbindPasskey(credentialId: string) {
  return fetchApi(`/auth/unbind-passkey/${credentialId}`, {
    method: 'DELETE',
  });
}

// ========== Registration Link Management ==========

export interface RegistrationLink {
  id: number;
  token: string;
  createdBy: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  createdAt: string;
  isActive: boolean;
  creator: {
    name: string | null;
    email: string | null;
    username: string | null;
  };
}

export function getRegistrationLinks() {
  return fetchApi<RegistrationLink[]>('/auth/registration-links');
}

export function createRegistrationLink(data: {
  maxUses?: number | null;
  expiresInHours?: number | null;
}) {
  return fetchApi<{ link: string }>('/auth/registration-links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function deleteRegistrationLink(token: string) {
  return fetchApi(`/auth/registration-links/${token}`, {
    method: 'DELETE',
  });
}

// ========== Reset Passkey Link Management ==========

export interface ResetPasskeyLink {
  id: number;
  token: string;
  userId: number;
  createdBy: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  createdAt: string;
  isActive: boolean;
  user: {
    id: number;
    email: string | null;
    username: string | null;
    name: string | null;
  };
  creator: {
    name: string | null;
  };
}

export function getResetPasskeyLinks() {
  return fetchApi<ResetPasskeyLink[]>('/auth/reset-passkey-links');
}

export function createResetPasskeyLink(data: {
  userId: number;
  expiresInHours?: number | null;
}) {
  return fetchApi<{ link: string }>('/auth/reset-passkey-links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function deleteResetPasskeyLink(token: string) {
  return fetchApi(`/auth/reset-passkey-links/${token}`, {
    method: 'DELETE',
  });
}

// ========== User Registration ==========

export function registerUser(data: {
  email?: string;
  username: string;  // Required
  password: string;  // Required
  name?: string;
  role?: string;
  managerId?: number;
  token?: string;
}) {
  return fetchApi<{ userId: number }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ========== Passkey Registration ==========

import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/types';

export type RegisterOptions = PublicKeyCredentialCreationOptionsJSON & { challengeId: string };

export function getRegisterOptions(userId: number | string, email?: string) {
  const params = email ? `?email=${encodeURIComponent(email)}` : '';
  return fetchApi<RegisterOptions & { challengeId: string }>(`/auth/register-options/${userId}${params}`);
}

export function getResetRegisterOptions(userId: number | string, email?: string, token?: string) {
  const params = new URLSearchParams();
  if (email) params.append('email', email);
  if (token) params.append('token', token);
  const queryString = params.toString();
  const url = queryString
    ? `/auth/reset-register-options/${userId}?${queryString}`
    : `/auth/reset-register-options/${userId}`;
  return fetchApi<RegisterOptions & { challengeId: string }>(url);
}

export function verifyRegistration(data: {
  userId: number;
  challengeId: string;
  response: any;
}) {
  return fetchApi('/auth/verify-registration', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ========== Token Validation ==========

export function validateRegistrationToken(token: string) {
  return fetchApi<{ valid: boolean; error?: string }>(
    `/auth/validate-registration-token?token=${encodeURIComponent(token)}`
  );
}

export function validateResetToken(token: string) {
  return fetchApi<{
    valid: boolean;
    error?: string;
    userId?: number;
    user?: { email?: string; username?: string; name?: string };
  }>(`/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
}

// ========== Passkey Reset ==========

export function resetPasskey(data: {
  token: string;
  userId: number;
  challengeId: string;
  response: any;
}) {
  return fetchApi('/auth/reset-passkey', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ========== Password Reset Link Management ==========

export interface PasswordResetLink {
  id: number;
  token: string;
  userId: number;
  createdBy: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  createdAt: string;
  isActive: boolean;
  user: {
    id: number;
    email: string | null;
    username: string | null;
    name: string | null;
  };
  creator: {
    name: string | null;
  };
}

export function getPasswordResetLinks() {
  return fetchApi<PasswordResetLink[]>('/auth/password-reset-links');
}

export function createPasswordResetLink(data: {
  userId: number;
  expiresInHours?: number | null;
}) {
  return fetchApi<{ link: string }>('/auth/password-reset-links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function deletePasswordResetLink(token: string) {
  return fetchApi(`/auth/password-reset-links/${token}`, {
    method: 'DELETE',
  });
}

export function validatePasswordResetToken(token: string) {
  return fetchApi<{
    valid: boolean;
    error?: string;
    userId?: number;
    user?: { email?: string; username?: string; name?: string };
  }>(`/auth/validate-password-reset-token?token=${encodeURIComponent(token)}`);
}

export function resetPassword(data: {
  token: string;
  userId: number;
  newPassword: string;
}) {
  return fetchApi('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
